import { useEffect, useState } from "react";
import {
  Box,
  Accordion,
  AccordionControlProps,
  ActionIcon,
  Flex,
  useMantineTheme,
  Text,
} from "@mantine/core";
import { IconPlaylistAdd } from "@tabler/icons";
import {
  CategoryOptionType,
  CreateCategoryOptionMutation,
  CreateCategoryOptionMutationVariables,
  DeleteCategoryOptionMutation,
  DeleteCategoryOptionMutationVariables,
  GetOrganizationQuery,
  UpdateCategoryOptionMutation,
  UpdateCategoryOptionMutationVariables,
} from "src/generated/graphql";
import { BasicFormProps } from "./BasicForm";
import { useApolloClient } from "@apollo/client";
import {
  CreateCategoryOption,
  DeleteCategoryOption,
  UpdateCategoryOption,
} from "src/api/graphql/mutations/Category";
import CategoryOptionList, {
  ICategoryOptionAction,
} from "src/components/CategoryOptionList";
import { useModals } from "@mantine/modals";
import NameInputForm from "src/components/NameInputForm";
import { object, string } from "yup";
import useWritePermission from "src/hooks/usePermission";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

type CategoryOptionObj = {
  [type in CategoryOptionType]: {
    id: string;
    name: string;
    isActive: boolean;
  }[];
};

const arrToObjCategoryOptions = (
  arr: NonNullable<GetOrganizationQuery["organizationOne"]>["categoryOptions"]
): CategoryOptionObj => {
  const reducedArrToObj = arr.reduce((acc, curr) => {
    const { type, name, id, isActive } = curr;
    return { ...acc, [type]: [...(acc[type] || []), { name, id, isActive }] };
  }, {} as CategoryOptionObj);
  return reducedArrToObj;
};

const organizationFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should have at least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
});

export const Sidebar = ({ organizationData, refetch }: BasicFormProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgSettings);
  const [orgCategoryOptions, setOrgCategoryOptions] =
    useState<CategoryOptionObj>();
  const { mutate } = useApolloClient();
  const modals = useModals();
  const theme = useMantineTheme();
  let color = theme.colors.primaryTextColor[6];

  const [accordionCategory, setAccordionCategory] = useState<string | null>(
    CategoryOptionType.Fee
  );

  useEffect(() => {
    setOrgCategoryOptions(
      arrToObjCategoryOptions(organizationData.categoryOptions)
    );
  }, [organizationData]);

  // === Handle APIs functions
  const createCategoryOption = async (
    name: string,
    type: CategoryOptionType
  ) => {
    try {
      await mutate<
        CreateCategoryOptionMutation,
        CreateCategoryOptionMutationVariables
      >({
        mutation: CreateCategoryOption,
        variables: {
          data: {
            name,
            organization: {
              id: organizationData.id,
            },
            type,
          },
        },
      });
      refetch();
    } catch (e) {
      throw new Error();
    }
  };

  const updateCategoryOption = async (id: string, name: string) => {
    try {
      await mutate<
        UpdateCategoryOptionMutation,
        UpdateCategoryOptionMutationVariables
      >({
        mutation: UpdateCategoryOption,
        variables: {
          updateCategoryOptionId: id,
          data: {
            name,
          },
        },
      });
      refetch();
    } catch (e) {
      throw new Error();
    }
  };

  const deleteCategoryOption = async (id: string) => {
    try {
      await mutate<
        DeleteCategoryOptionMutation,
        DeleteCategoryOptionMutationVariables
      >({
        mutation: DeleteCategoryOption,
        variables: {
          deleteCategoryOptionId: id,
        },
      });
      refetch();
    } catch (e) {
      throw new Error();
    }
  };

  const onCreate = (type: CategoryOptionType) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Create ${type}`,
      children: (
        <NameInputForm
          schema={organizationFormSchema}
          onSubmit={(values) => {
            createCategoryOption(values.name, type);
            setAccordionCategory(type);
            modals.closeAll();
          }}
          onCancel={() => modals.closeAll()}
          type={type}
        />
      ),
    });
  };

  const onEdit = ({ id, name, type }: ICategoryOptionAction) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Edit ${type}`,
      children: (
        <NameInputForm
          schema={organizationFormSchema}
          onSubmit={(values) => {
            updateCategoryOption(id, values.name);
            modals.closeAll();
          }}
          value={name}
          onCancel={() => modals.closeAll()}
          type={type}
        />
      ),
    });
  };

  const onDelete = ({ id, name, type }: ICategoryOptionAction) => {
    modals.openContextModal("delete", {
      closeOnClickOutside: false,
      title: `Confirm Delete ${type}`,
      centered: true,
      innerProps: {
        innerText: `If you remove this option, it will no longer be selected anywhere else in
          the app. Are you sure you want to delete the selected option, ${name}?`,
        onDelete: () => deleteCategoryOption(id),
      },
    });
  };

  const OrganizationTabLabels = {
    Fee: "Fee Types",
    Agreement: "Agreement Tags",
    Revenue: "Revenue Groups",
    Service: "Service Types",
    Tag: "General Tags",
  };

  const AccordionControl = (props: AccordionControlProps) => {
    const { title } = props;
    const type: CategoryOptionType = title as CategoryOptionType;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justify: "space-between",
        }}
      >
        <Accordion.Control {...props} />
        {isAllowWrite && (
          <ActionIcon
            size="lg"
            onClick={() => {
              onCreate(type);
            }}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.primary[0],
              },
            }}
          >
            <IconPlaylistAdd color={color} />
          </ActionIcon>
        )}
      </Box>
    );
  };

  return (
    <Box
      m={0}
      sx={{
        "& .mantine-Paper-root.mantine-Card-root:first-of-type": {
          borderRadius: "8px 8px 0 0",
        },
        "& .mantine-Paper-root.mantine-Card-root:last-child": {
          borderRadius: "0 0 8px 8px",
        },
      }}
    >
      <Accordion
        variant="contained"
        chevronPosition="left"
        maw={400}
        mx="auto"
        value={accordionCategory}
        onChange={setAccordionCategory}
        sx={{
          ".mantine-Accordion-panel": {
            backgroundColor: "#fff",
            borderTop: "1px solid #ddd",
          },
        }}
      >
        {orgCategoryOptions &&
          Object.values(CategoryOptionType).map((type) => (
            <Accordion.Item
              value={type}
              key={type}
              style={{
                backgroundColor: theme.colors.primary[6],
                borderColor: theme.colors.primary[6],
              }}
            >
              <AccordionControl
                title={type}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[2],
                    borderRadius: 4,
                  },
                }}
              >
                <Flex justify="space-between" align="center">
                  <Text weight={500} color={color}>
                    {OrganizationTabLabels[type]}
                  </Text>
                </Flex>
              </AccordionControl>

              <Accordion.Panel
                style={{
                  backgroundColor: theme.colors.secondary[6],
                  color: theme.colors.secondaryTextColor[6],
                }}
              >
                <CategoryOptionList
                  key={type}
                  type={type}
                  options={orgCategoryOptions[type]}
                  onCreate={onCreate}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isAllowWrite={isAllowWrite}
                  refetch={refetch}
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
      </Accordion>
    </Box>
  );
};
